import React, { FC, Fragment, useState } from 'react';
import { formatDate } from 'utils/format';
import { useNavigate } from 'react-router-dom';
import { TableCell, TableRow, Box, Typography, IconButton, Tooltip } from '@mui/material';
import ReceiptLongOutlinedIcon from '@mui/icons-material/ReceiptLongOutlined';
import RequestQuoteOutlinedIcon from '@mui/icons-material/RequestQuoteOutlined';
import { useTranslation } from 'react-i18next';
import ActiveIndicator from 'components/users/shared/active-indicator/active-indicator.component';
import ImportFileDialog from 'components/shared/import-file-dialog/import-file-dialog.component';
import FormattedAddress from 'components/shared/address/formatted-address.component';
import { Lab } from 'models/lab';
import ArticleIcon from '@mui/icons-material/Article';
import PublishIcon from '@mui/icons-material/Publish';
import LocalShippingIcon from '@mui/icons-material/LocalShipping';
import CloudSyncIcon from '@mui/icons-material/CloudSync';
import SettingsIcon from '@mui/icons-material/Settings';

type LabItemProps = {
    lab: Lab;
    onEditClinicClick: (lab: Lab) => void;
    onTransportSettingClick: (lab: Lab) => void;
    onHHSyncClick?: (clinic: Lab) => void;
    onTemplateClick: (lab: Lab) => void;
};
const LabItem: FC<LabItemProps> = ({
    lab,
    onEditClinicClick,
    onTransportSettingClick,
    onHHSyncClick,
    onTemplateClick,
}: LabItemProps) => {
    const { t: translate } = useTranslation(['common', 'users']);
    const [isImportDialogOpen, setIsImportDialogOpen] = useState(false);
    const navigate = useNavigate();

    const handleTransportSettingsClick = (event: React.MouseEvent<HTMLElement>, lab: Lab) => {
        event.stopPropagation();
        event.preventDefault();
        onTransportSettingClick(lab);
    };

    return (
        <Fragment>
            <TableRow hover sx={{ cursor: 'pointer' }} onClick={() => onEditClinicClick(lab)}>
                <TableCell sx={{ width: 10 }} align="center">
                    <ActiveIndicator isActive={!!lab?.active} />
                </TableCell>
                <TableCell sx={{ width: 300 }}>
                    <Box>{lab.name}</Box>
                    <Typography variant="caption" color="grey.500">
                        {lab.officialName}
                    </Typography>
                </TableCell>
                <TableCell>
                    <Box>{lab.organizationNumber}</Box>{' '}
                    <Typography variant="caption" color="grey">
                        {lab.hitHorizonsId}
                    </Typography>
                </TableCell>
                <TableCell>
                    <FormattedAddress address={lab.contact?.address} oneLineFormat />
                </TableCell>
                <TableCell>{lab.created?.on && <div>{formatDate(new Date(lab.created.on), 'date')}</div>}</TableCell>
                <TableCell>
                    <Box display="flex" justifyContent="flex-end">
                        <Tooltip title={translate('users:IMPORT').toString()} placement="bottom">
                            <IconButton
                                onClick={(e) => {
                                    e.stopPropagation();
                                    setIsImportDialogOpen(true);
                                }}
                                aria-label="template"
                                size="small"
                            >
                                <PublishIcon />
                            </IconButton>
                        </Tooltip>

                        <Tooltip title={translate('organization:PDF_TEMPLATE').toString()} placement="bottom">
                            <IconButton
                                onClick={(e) => {
                                    e.stopPropagation();
                                    onTemplateClick(lab);
                                }}
                                aria-label="template"
                                size="small"
                            >
                                <ArticleIcon />
                            </IconButton>
                        </Tooltip>

                        <Tooltip title={translate('navigation:TRANSPORT').toString()} placement="bottom">
                            <IconButton
                                onClick={(e) => handleTransportSettingsClick(e, lab)}
                                aria-label="template"
                                size="small"
                            >
                                <LocalShippingIcon />
                            </IconButton>
                        </Tooltip>

                        <Tooltip title={translate('organization:HIT_HORIZONS').toString()} placement="bottom">
                            <IconButton
                                onClick={(e) => {
                                    e.stopPropagation();
                                    if (onHHSyncClick) {
                                        onHHSyncClick(lab);
                                    }
                                }}
                                aria-label="template"
                                size="small"
                            >
                                <CloudSyncIcon />
                            </IconButton>
                        </Tooltip>

                        <Tooltip title={translate('super-admin:MENU_ORDERS').toString()} placement="bottom">
                            <IconButton
                                onClick={(event) => {
                                    event.stopPropagation();
                                    navigate(
                                        `/orders?supplier=${lab._id}&supplierName=${encodeURIComponent(lab.name ?? '')}`,
                                    );
                                }}
                                aria-label="orders"
                                size="small"
                                data-test="lab-item.orders"
                            >
                                <ReceiptLongOutlinedIcon />
                            </IconButton>
                        </Tooltip>

                        <Tooltip title={translate('super-admin:MENU_INVOICES').toString()} placement="bottom">
                            <IconButton
                                onClick={(event) => {
                                    event.stopPropagation();
                                    navigate(`/invoices?lab=${lab._id}&labName=${encodeURIComponent(lab.name ?? '')}`);
                                }}
                                aria-label="invoices"
                                size="small"
                                data-test="lab-item.invoices"
                            >
                                <RequestQuoteOutlinedIcon />
                            </IconButton>
                        </Tooltip>

                        <Tooltip title={translate('common:SETTINGS').toString()} placement="bottom">
                            <IconButton
                                onClick={(e) => {
                                    e.stopPropagation();
                                    navigate(`/customer-settings/${lab._id}`);
                                }}
                                aria-label="template"
                                size="small"
                            >
                                <SettingsIcon />
                            </IconButton>
                        </Tooltip>
                    </Box>
                </TableCell>
            </TableRow>
            <ImportFileDialog
                open={isImportDialogOpen}
                onCloseDialog={() => setIsImportDialogOpen(false)}
                labId={lab._id}
            />
        </Fragment>
    );
};

export default LabItem;
